import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";

import { Product } from "@dashboard/types/product";
import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { ErrorReducerAction } from "../reducers/errorReducer";
import { ProductReducerAction } from "../reducers/productReducer";
import { addError } from "./addErrorAction";
import { fetchResolvedEdibles } from "./resolvedEdibleActions";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction
>;

export type ProductThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction | ErrorReducerAction
>;

export const setProduct: (
  product: Product,
  isNew: boolean
) => ThunkActionReturn = (product: Product, isNew: boolean) => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    return (
      productApi
        .setProduct(product, isNew)
        // refresh edible products after each change, since that data is now stale
        // resolvedEdibles state must be updated before this new product is added
        // to the state, to ensure all UI compoenents find the new product
        // in the resolvedEdibles state
        .then(() => dispatch(fetchResolvedEdibles()))
        .then(() => {
          dispatch({ type: "SET_PRODUCT", payload: product });
        })
        .catch(addError(dispatch))
    );
  };
};

export const deleteProduct: (
  restaurantUuid: string,
  productId: string
) => ThunkActionReturn = (restaurantUuid: string, productId: string) => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    return (
      productApi
        .deleteProduct(restaurantUuid, productId)
        // note remove the product from the state after the API call is successful
        // so that it is no longer rendered in the UI
        // then call resolvedEdibles to refresh the data (it is now stale)
        // We don't refresh the resolvedEdibles first as it would then have a missing product
        .then(() => dispatch({ type: "DELETE_PRODUCT", payload: productId }))
        .then(() => dispatch(fetchResolvedEdibles()))
        .catch(addError(dispatch))
    );
  };
};

// Downloads all products
// We don't use snapshotListeners here because we don't want to keep listening to changes
export const fetchProducts: () => ThunkActionReturn = () => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    // get current restaurant from state. this allows for running fetchProducts
    // as a refresh operation even when restaurants are changing.
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return productApi
      .fetchProducts(currentRest.uuid)
      .then((products) => {
        dispatch({ type: "FETCHED_PRODUCTS", payload: products });
      })
      .catch(addError(dispatch));
  };
};
